// sapi-admin/src/pages/WhatsAppAdmin.jsx
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { sendWhatsAppTemplate } from '../services/whatsappService';

const WhatsAppAdmin = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('order_placed');
  const [templateParams, setTemplateParams] = useState({});
  const [sending, setSending] = useState(false);

  // ✅ Templates approuvés dans Meta
  const templates = [
    {
      id: 'order_placed',
      name: '📦 Order Confirmation',
      description: 'Confirmation de commande',
      params: [
        { key: 'var1', label: 'Nom client', default: 'Client' },
        { key: 'var2', label: 'Order #', default: 'CMD-123' },
        { key: 'var3', label: 'Montant (π)', default: '0.5' },
        { key: 'var4', label: 'Nombre articles', default: '1' },
        { key: 'var5', label: 'Délai', default: 'Standard - 7 days' },
        { key: 'var6', label: 'Adresse', default: 'Douala, Cameroon' },
        { key: 'var7', label: 'Frais livraison', default: '0.5 π' },
      ],
      paramsCount: 7
    },
    {
      id: 'order_packed',
      name: '📦 Order Packed',
      description: 'Commande emballée et prête pour l\'expédition',
      params: [
        { key: 'var1', label: 'Nom client', default: 'Client' },
        { key: 'var2', label: 'Order #', default: 'CMD-123' },
      ],
      paramsCount: 2
    },
    {
      id: 'order_shipped',
      name: '📦 Order Shipped',
      description: 'Commande expédiée',
      params: [
        { key: 'var1', label: 'Order #', default: 'CMD-123' },
        { key: 'var2', label: 'Nom client', default: 'Client' },
        { key: 'var3', label: 'Transporteur', default: 'Our delivery partner' },
        { key: 'var4', label: 'Tracking', default: 'N/A' },
        { key: 'var5', label: 'URL tracking', default: 'sapi.etralis.com' },
      ],
      paramsCount: 5
    },
    {
      id: 'order_delivered',
      name: '✅ Order Delivered',
      description: 'Commande livrée',
      params: [
        { key: 'var1', label: 'Nom client', default: 'Client' },
        { key: 'var2', label: 'Order #', default: 'CMD-123' },
      ],
      paramsCount: 2
    },
    {
      id: 'order_approved',
      name: '✅ Order Approved',
      description: 'Commande approuvée',
      params: [
        { key: 'var1', label: 'Nom client', default: 'Client' },
        { key: 'var2', label: 'Order #', default: 'CMD-123' },
      ],
      paramsCount: 2
    },
    {
      id: 'order_cancelled',
      name: '❌ Order Cancelled',
      description: 'Commande annulée',
      params: [
        { key: 'var1', label: 'Nom client', default: 'Client' },
        { key: 'var2', label: 'Order #', default: 'CMD-123' },
      ],
      paramsCount: 2
    },
    {
      id: 'eneo_rech',
      name: '⚡ ENEO Recharge',
      description: 'Recharge ENEO confirmée',
      params: [
        { key: 'var1', label: 'Nom client', default: 'Client' },
        { key: 'var2', label: 'Compteur', default: '014277229384' },
        { key: 'var3', label: 'kWh', default: '10' },
        { key: 'var4', label: 'Montant FCFA', default: '1000' },
        { key: 'var5', label: 'Pi', default: '1.5 π' },
        { key: 'var6', label: 'Token', default: '1234-5678-9012' },
      ],
      paramsCount: 6
    },
    {
      id: 'pi_sale_paid',
      name: '💰 Pi Sale Paid',
      description: 'Vente Pi payée',
      params: [
        { key: 'var1', label: 'Nom client', default: 'Client' },
        { key: 'var2', label: 'Pi amount', default: '1.5 π' },
        { key: 'var3', label: 'Taux', default: '120' },
        { key: 'var4', label: 'Total FCFA', default: '180' },
        { key: 'var5', label: 'Payout', default: '180' },
        { key: 'var6', label: 'Opérateur', default: 'MTN Cameroon' },
        { key: 'var7', label: 'Numéro', default: '677077030' },
      ],
      paramsCount: 7
    },
    {
      id: 'stor_activated',
      name: '🏪 Store Activated',
      description: 'Boutique activée',
      params: [
        { key: 'var1', label: 'Nom propriétaire', default: 'Partenaire' },
        { key: 'var2', label: 'Nom boutique', default: 'Ma Boutique' },
      ],
      paramsCount: 2
    },
    {
      id: 'store_verified',
      name: '✅ Store Verified',
      description: 'Boutique vérifiée',
      params: [
        { key: 'var1', label: 'Nom propriétaire', default: 'Partenaire' },
        { key: 'var2', label: 'Nom boutique', default: 'Ma Boutique' },
      ],
      paramsCount: 2
    },
  ];

  const currentTemplate = templates.find(t => t.id === selectedTemplate);

  const handleSend = async () => {
    if (!phoneNumber) {
      toast.warning('Veuillez entrer un numéro de téléphone');
      return;
    }

    // Récupérer les paramètres dans l'ordre
    const params = currentTemplate.params.map(p => templateParams[p.key] || p.default);

    setSending(true);
    try {
      const result = await sendWhatsAppTemplate(
        phoneNumber,
        selectedTemplate,
        params,
        'en'
      );
      
      if (result.success) {
        toast.success(`✅ Template "${currentTemplate.name}" envoyé à ${phoneNumber}`);
      } else {
        toast.error(`❌ Échec: ${result.error}`);
        console.error('Erreur:', result.error);
      }
    } catch (error) {
      console.error('❌ Erreur:', error);
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handleParamChange = (key, value) => {
    setTemplateParams(prev => ({ ...prev, [key]: value }));
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <svg className="w-8 h-8 text-emerald-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            WhatsApp Administration
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Envoyer des notifications WhatsApp avec des templates approuvés
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Send form */}
        <div className="lg:col-span-2 bg-[#111827]/80 rounded-2xl p-6 ring-1 ring-white/[0.06]">
          <h2 className="text-lg font-semibold text-white mb-4">📤 Envoyer un message</h2>
          
          <div className="space-y-4">
            <div>
              <label className="text-xs text-slate-500 block mb-1">
                Numéro de téléphone
                <span className="text-amber-400 ml-1">*</span>
              </label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="2376XXXXXXXX"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white 
                         focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Format: 2376XXXXXXXX (sans +)
              </p>
            </div>

            <div>
              <label className="text-xs text-slate-500 block mb-1">
                Template
                <span className="text-amber-400 ml-1">*</span>
              </label>
              <select
                value={selectedTemplate}
                onChange={(e) => {
                  setSelectedTemplate(e.target.value);
                  setTemplateParams({});
                }}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white
                         focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
              >
                {templates.map(t => (
                  <option key={t.id} value={t.id} className="bg-[#111827]">
                    {t.name} ({t.paramsCount} variables)
                  </option>
                ))}
              </select>
            </div>

            {currentTemplate && (
              <div className="space-y-3 bg-white/5 rounded-xl p-4">
                <p className="text-xs text-slate-400">
                  Paramètres du template ({currentTemplate.paramsCount} variables) :
                </p>
                {currentTemplate.params.map((param) => (
                  <div key={param.key}>
                    <label className="text-xs text-slate-500 block mb-1">
                      {param.label}
                    </label>
                    <input
                      type="text"
                      value={templateParams[param.key] || ''}
                      onChange={(e) => handleParamChange(param.key, e.target.value)}
                      placeholder={param.default}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white text-sm
                               focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                    />
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={handleSend}
              disabled={sending || !phoneNumber}
              className="w-full bg-emerald-500/20 hover:bg-emerald-500/30 
                       text-emerald-400 font-semibold py-3 px-6 rounded-xl
                       transition-all duration-200 disabled:opacity-50 
                       disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {sending ? (
                <>
                  <div className="w-4 h-4 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Envoyer le template
                </>
              )}
            </button>
          </div>
        </div>

        {/* Templates list */}
        <div className="bg-[#111827]/80 rounded-2xl p-6 ring-1 ring-white/[0.06]">
          <h2 className="text-lg font-semibold text-white mb-4">📝 Templates disponibles</h2>
          
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {templates.map((t) => (
              <button
                key={t.id}
                onClick={() => {
                  setSelectedTemplate(t.id);
                  setTemplateParams({});
                }}
                className={`w-full text-left p-3 rounded-xl transition-all duration-200 ${
                  selectedTemplate === t.id
                    ? 'bg-emerald-500/20 ring-1 ring-emerald-500/50'
                    : 'bg-white/5 hover:bg-white/10'
                }`}
              >
                <p className="text-sm font-semibold text-slate-300">{t.name}</p>
                <p className="text-xs text-slate-500">{t.description}</p>
                <p className="text-[10px] text-emerald-400 mt-1">{t.paramsCount} variables</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default WhatsAppAdmin;